/* Dual-handle range sliders for the stone browse panel.
   Progressive enhancement only — see the "FALLBACK" note below.

   ── PRICE AND CARAT ARE NOT THE SAME KIND OF FILTER ──────────────────────────
   This matters more than the shared UI suggests.

   PRICE is a native Shopify RANGE filter (`filter.v.price.gte` / `.lte`), so it is
   genuinely continuous: any two numbers work and the slider is a true slider.

   CARAT IS A **LIST** FILTER OF PRE-BUCKETED STRINGS, NOT A NUMBER.
   `filter.p.m.custom.carat_weight` holds "0.01 - 0.50", "0.51 - 1.00", ... — 13 of
   them on sapphires. Shopify list filters match exact values only, so no continuous
   carat query exists to send. The slider therefore SNAPS to bucket edges and selects
   every bucket between the handles (list filters multi-select, which is what makes
   this work at all). Granularity is 0.5ct and cannot be finer without new data —
   `custom.carat_weight` would have to become a `number_decimal` metafield exposed
   as a range filter in Search & Discovery.

   ⚠️ THE BUCKETS ARE NOT CONTIGUOUS. Measured: alexandrite has 0.01-0.50,
   0.51-1.00, 1.01-1.50 and then jumps straight to 3.01-3.50; garnet starts at 0.51,
   not 0.01. So the handles index the AVAILABLE buckets in order rather than a
   linear carat axis — dragging across a gap selects the buckets that exist and
   nothing is invented. Never compute a bucket from `(carat - min) / 0.5`.

   ── FALLBACK ────────────────────────────────────────────────────────────────
   The server renders the chips and the price form, which work with no JS at all.
   This file only ADDS a slider and hides what it replaces, by putting `is-upgraded`
   on the host. If this script fails to parse, or a browser refuses the custom
   element, the panel is exactly as functional as it was before sliders existed. Do
   not invert that.

   ⚠️ AS OF 2026-08-02 THE SLIDER REPLACES THE PRICE FORM TOO, NOT JUST THE CHIPS.
   Each end of the track carries an editable field, so the form beside it was the
   same control twice — and it was the half with the "Go" button. Typed values apply
   on `change` (blur or Enter), which is why no button is needed. The form is hidden
   by CSS, never removed: with JS off it is still the only way to enter a price.

   ── WHY TWO NATIVE <input type="range"> AND NOT A DIV I DRAG ─────────────────
   Keyboard operation, touch, screen-reader announcement of the current value and
   the focus ring all come free and correct. A hand-rolled drag handler gets the
   pointer maths right and every one of those wrong. The two inputs are stacked and
   `pointer-events` is switched per-thumb so each stays grabbable. */
(function () {
  'use strict';

  // ⚠️ 'en-US' IS PINNED, NOT THE VISITOR'S LOCALE. These numbers are printed INTO a
  // field that parse() reads back, so the two must agree on what a separator is. On
  // a de-DE browser `undefined` formats 26345 as "26.345", which parse() then reads
  // as 26.345 — the ceiling collapses by a factor of a thousand for German visitors
  // and nobody else. The store's own money format is US-style ($235), so this also
  // keeps the fields consistent with every other price on the storefront.
  var money = function (n) {
    return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  customElements.get('gem-range') || customElements.define('gem-range',
    class extends HTMLElement {
      connectedCallback() {
        this.kind = this.dataset.kind;                 // 'price' | 'band'
        this.unit = this.dataset.unit || '';           // ' ct' | ' mm'
        try {
          this.stops = JSON.parse(this.dataset.stops || 'null');
        } catch (e) {
          this.stops = null;
        }
        // A banded slider with fewer than two buckets is not a range — leave the
        // chips alone rather than shipping a slider that cannot move.
        if (this.kind === 'band' && (!this.stops || this.stops.length < 2)) return;

        this.lo = this.kind === 'band' ? 0 : Number(this.dataset.min || 0);
        this.hi = this.kind === 'band'
          ? this.stops.length - 1
          : Number(this.dataset.max || 0);
        if (!(this.hi > this.lo)) return;              // degenerate range

        // ⚠️ THE CEILING IS RAISED TO A WHOLE STEP, AND WITHOUT THIS THE MOST
        // EXPENSIVE STONE IS UNREACHABLE. A range input can only sit on
        // `min + n*step`, so a max that is not a multiple of the step is clamped
        // DOWN to the one below it. Sapphires top out at $26,345 and the step over
        // that span is 10, so the handle stopped at 26,340: the field printed
        // "$26,340" as the maximum, and — far worse — `urlFor()` then saw
        // `b < this.hi` and applied `lte=26340`, so merely nudging the MINIMUM
        // silently excluded the $26,345 stone the shopper could see on the page
        // (owner, 2026-08-02).
        //
        // Rounding UP rather than snapping the value down keeps the top of the track
        // meaning "no upper limit": at the ceiling `b === this.hi`, so no `lte` is
        // emitted at all and nothing can be cut off. The printed figure sits a few
        // dollars above the dearest stone, which is what a slider bound is.
        //
        // Band sliders are indices with step 1 and are always aligned already.
        this.step = this.kind === 'band' ? 1 : this.stepFor();
        if (this.kind !== 'band') {
          var span = this.hi - this.lo;
          this.hi = this.lo + Math.ceil(span / this.step) * this.step;
        }

        this.curLo = this.dataset.curMin !== '' && this.dataset.curMin != null
          ? Number(this.dataset.curMin) : this.lo;
        this.curHi = this.dataset.curMax !== '' && this.dataset.curMax != null
          ? Number(this.dataset.curMax) : this.hi;

        this.build();
        this.classList.add('is-upgraded');
      }

      // The row's own label ("Length", "Carat", "Price"), read off the panel rather
      // than passed in, so the three `sb-band-row` callers need no new argument. The
      // <em> sub-label ("mm, every stone") is dropped — it qualifies the row, and
      // repeating it inside every field name would make the announcement twice as
      // long for no extra information.
      labelText() {
        var row = this.closest('.sb__row');
        var l = row && row.querySelector('.sb__label');
        if (l) {
          var em = l.querySelector('em');
          var t = l.textContent || '';
          if (em) t = t.replace(em.textContent || '', '');
          return t.trim();
        }
        // Inside a dropdown there is no row and no `.sb__label` — the trigger is the
        // label. Without this the fields announce as a bare "minimum"/"maximum" with
        // no axis, which is the one thing a screen reader needs most here.
        var dd = this.closest('.sb-dd');
        var k = dd && dd.querySelector('.sb-dd__k');
        return k ? (k.textContent || '').trim() : '';
      }

      fieldHtml(side) {
        return '<span class="sb-rng__f sb-rng__f--' + side + '">' +
          // The symbol box is emitted EMPTY and filled by textContent below.
          (this.kind === 'band'
            ? '' : '<span class="sb-rng__sym" aria-hidden="true"></span>') +
          '<input class="sb-rng__in sb-rng__in--' + side + '" type="text" ' +
            'inputmode="decimal" autocomplete="off" spellcheck="false">' +
        '</span>';
      }

      build() {
        // Cached in connectedCallback, NOT recomputed: `stepFor()` reads the span,
        // and the span has since been rounded up by one step. Calling it again would
        // work today but makes the ceiling depend on a value derived from itself.
        var step = this.step;
        this.innerHTML =
          '<div class="sb-rng">' +
            '<div class="sb-rng__track"><div class="sb-rng__fill"></div></div>' +
            '<input class="sb-rng__i sb-rng__i--lo" type="range" aria-label="Minimum">' +
            '<input class="sb-rng__i sb-rng__i--hi" type="range" aria-label="Maximum">' +
          '</div>' +
          '<div class="sb-rng__out">' +
            this.fieldHtml('lo') + this.fieldHtml('hi') +
          '</div>';

        this.a = this.querySelector('.sb-rng__i--lo');
        this.b = this.querySelector('.sb-rng__i--hi');
        this.fill = this.querySelector('.sb-rng__fill');
        this.inLo = this.querySelector('.sb-rng__in--lo');
        this.inHi = this.querySelector('.sb-rng__in--hi');

        // ⚠️ NEITHER THE SYMBOL NOR THE ROW LABEL IS INTERPOLATED INTO innerHTML.
        // One is `{{ cart.currency.symbol }}` and the other is scraped out of the
        // DOM; building markup from either would make them an injection surface for
        // no benefit at all, when textContent/setAttribute do the same job.
        var name = this.labelText();
        this.inLo.setAttribute('aria-label', (name ? name + ' ' : '') + 'minimum');
        this.inHi.setAttribute('aria-label', (name ? name + ' ' : '') + 'maximum');
        Array.prototype.forEach.call(this.querySelectorAll('.sb-rng__sym'),
          function (s) { s.textContent = this.dataset.symbol || '$'; }, this);

        [this.a, this.b].forEach(function (i) {
          i.min = this.lo; i.max = this.hi; i.step = step;
        }, this);
        this.a.value = this.curLo;
        this.b.value = this.curHi;

        var live = this.onInput.bind(this);
        [this.a, this.b].forEach(function (i) {
          i.addEventListener('input', live);
          // Commit on RELEASE, not on input: a range input fires continuously while
          // dragging, and navigating on every tick would fire dozens of page loads.
          i.addEventListener('change', this.commit.bind(this));
        }, this);

        // ⚠️ `change`, NOT `input` — THIS IS WHAT REPLACES THE "GO" BUTTON. A text
        // field fires `input` on every keystroke, so committing there would navigate
        // away mid-number: typing "150" would reload on "1". `change` fires exactly
        // when a typed value is FINISHED — on blur, and on Enter — which is why no
        // button is needed to say "done". Enter is turned into a blur so that both
        // routes run the identical path rather than two that can drift.
        var self = this;
        [[this.inLo, 'lo'], [this.inHi, 'hi']].forEach(function (pair) {
          // `input` only moves the thumb, `change` is the one that applies.
          pair[0].addEventListener('input', function () { self.preview(pair[1], this.value); });
          pair[0].addEventListener('change', function () { self.typed(pair[1], this.value); });
          pair[0].addEventListener('keydown', function (e) {
            if (e.key === 'Enter') { e.preventDefault(); this.blur(); }
          });
        });
        this.paint();

        // ⚠️ THE CSS THAT HIDES THE PRICE FORM KEYS OFF THIS CLASS, NOT `is-upgraded`,
        // AND THAT IS NOT A STYLE CHOICE. The two files do not go live together: on
        // 2026-08-02 the new CSS was serving while this file was still the previous
        // build, and because the OLD build also sets `is-upgraded`, the form was
        // hidden by CSS that expected fields the running JS had never drawn — the
        // price row lost its numeric entry entirely for as long as the skew lasted.
        // A shopper with this file cached and the new CSS fresh hits the identical
        // state, so this is not just a deploy-window problem. `is-fielded` is set
        // HERE, at the point the fields provably exist, so the form can only ever be
        // hidden by something that has already replaced it.
        this.classList.add('is-fielded');
      }

      // Tolerant on purpose: shoppers paste "1,250", "$1250", "6.25 ct", and our own
      // fields print "16+" for an open-ended band and "26,345" for a ceiling.
      //
      // ⚠️ TAKE THE FIRST NUMBER, DO NOT STRIP-AND-CONCATENATE. Deleting every
      // non-digit turns a pasted RANGE into one huge number: "50-200" became 50200,
      // which then clamped to the ceiling and returned a nearly empty result set from
      // an input that looked entirely reasonable. Commas come out first (they are
      // separators inside a single number), then the first numeric run wins and any
      // trailing junk — "+", "ct", a second number — is ignored.
      parse(raw) {
        var m = String(raw).replace(/,/g, '').match(/\d+(?:\.\d+)?/);
        return m ? parseFloat(m[0]) : null;
      }

      // ⚠️ A BAND SLIDER CANNOT HONOUR A TYPED NUMBER EXACTLY, AND MUST NOT PRETEND
      // TO. Carat/length/width are Shopify LIST filters whose values are whole band
      // strings, so the only positions that exist are band EDGES — and the bands are
      // not contiguous (alexandrite jumps 1.50 -> 3.01), so the index cannot be
      // derived arithmetically from the number. Nearest edge wins, and paint() then
      // rewrites the field with the edge actually applied, so the figure on screen is
      // never one the filter is not using.
      nearestStop(side, v) {
        var best = side === 'lo' ? 0 : this.stops.length - 1;
        var bestD = Infinity;
        for (var i = 0; i < this.stops.length; i++) {
          var s = this.stops[i];
          var edge = parseFloat(side === 'lo' ? s.lo : (s.hi || s.lo));
          if (isNaN(edge)) continue;
          var d = Math.abs(edge - v);
          if (d < bestD) { bestD = d; best = i; }
        }
        return best;
      }

      // Where a typed number lands on the track: snapped for a band, clamped for a
      // continuous range, and never past the other handle. Shared by preview() and
      // typed() so the thumb you watch while typing is the value that gets applied.
      resolve(side, v) {
        var next = this.kind === 'band'
          ? this.nearestStop(side, v)
          : Math.min(Math.max(v, this.lo), this.hi);
        // Same no-crossing rule as onInput: a minimum above the maximum returns an
        // empty result set, which reads as a broken filter rather than an impossible
        // range. Typing is just the other way to move a handle.
        return side === 'lo'
          ? Math.min(next, Number(this.b.value))
          : Math.max(next, Number(this.a.value));
      }

      // Fires on every keystroke. Moves the thumb and the fill so the track answers
      // while you type — it used to sit still until the field lost focus, so the
      // slider looked broken until you clicked away. Deliberately does NOT commit:
      // navigating per keystroke would reload on the "1" of "150". paint() leaves the
      // focused field's text alone, so this never fights the typing.
      preview(side, raw) {
        var v = this.parse(raw);
        if (v === null) return;
        (side === 'lo' ? this.a : this.b).value = this.resolve(side, v);
        this.paint();
      }

      // One typed number applied to one handle. Always repaints, so the field ends up
      // showing what was APPLIED rather than what was asked for.
      //
      // ⚠️ DO NOT REINTRODUCE A BEFORE/AFTER HANDLE COMPARISON HERE. preview() has
      // already moved the thumb by the time `change` fires, so "did the handle move"
      // is always false by then and the commit never happens — typing a number would
      // slide the track and then quietly do nothing. Whether anything actually
      // changed is a question about the URL, and commit() already answers it.
      typed(side, raw) {
        var v = this.parse(raw);
        if (v === null) { this.paint(); return; }   // gibberish -> restore the real value
        (side === 'lo' ? this.a : this.b).value = this.resolve(side, v);
        this.paint();
        this.commit();
      }

      // A slider over a $2.6M ceiling with step 1 has no usable resolution at the
      // low end where nearly all the stock sits, and it also makes the thumb jitter.
      // Step scales with the range; the end fields take exact values, which is what
      // makes a coarse step harmless — nobody has to hit $1,337 by dragging.
      stepFor() {
        var span = this.hi - this.lo;
        if (span > 500000) return 1000;
        if (span > 50000) return 100;
        if (span > 5000) return 10;
        return 1;
      }

      onInput() {
        // Stop the handles crossing over. Without this the "min" can be dragged
        // past the "max" and the resulting query returns nothing, which reads as a
        // broken filter rather than an impossible range.
        var a = Number(this.a.value), b = Number(this.b.value);
        if (a > b) {
          if (document.activeElement === this.a) this.a.value = b;
          else this.b.value = a;
        }
        this.paint();
      }

      paint() {
        var a = Number(this.a.value), b = Number(this.b.value);
        var span = this.hi - this.lo || 1;
        this.fill.style.insetInlineStart = ((a - this.lo) / span * 100) + '%';
        this.fill.style.width = ((b - a) / span * 100) + '%';
        var lo, hi;
        if (this.kind === 'band') {
          // Show the real edges of the selected bands, not the indices — "1.01 –
          // 2.00 ct" is the number a shopper is thinking in. Leading zeros are
          // STRIPPED here: the mm bands are stored zero-padded ("008.0 - 008.5")
          // purely so Shopify's string sort matches numeric order, and no shopper
          // should ever be shown "008.0".
          var trim = function (x) { return String(x).replace(/^0+(?=\d)/, ''); };
          var s1 = this.stops[Math.round(a)], s2 = this.stops[Math.round(b)];
          lo = s1 ? trim(s1.lo) : '';
          // An empty `hi` marks the open-ended top band ("Over 16 mm"), shown as
          // "16+". ⚠️ NOT a bare "16": in a field that reads as a CEILING of 16,
          // i.e. the exact opposite of the filter it represents. parse() drops the
          // "+" again on the way back in.
          hi = s2 ? (s2.hi ? trim(s2.hi) : trim(s2.lo) + '+') : '';
        } else {
          // No symbol here — the field carries it in its own prefix box, the way
          // the old server-rendered price form did.
          lo = money(a);
          hi = money(b);
        }
        // ⚠️ NEVER YANK THE TEXT OUT FROM UNDER SOMEONE MID-EDIT. paint() also runs
        // while the OTHER handle is being dragged, and rewriting a focused field
        // would delete what is being typed into it.
        if (document.activeElement !== this.inLo) this.inLo.value = lo;
        if (document.activeElement !== this.inHi) this.inHi.value = hi;
      }

      // Rewrite the CURRENT url rather than composing one from scratch: every other
      // active filter and the sort order are already in it and must survive. This is
      // the JS equivalent of the `url_to_add` rule the link chips follow.
      commit() {
        var next = this.urlFor();
        // ⚠️ A COMMIT THAT CHANGES NOTHING MUST NOT RELOAD THE PAGE. `change` fires
        // when a thumb is released whether or not it moved, and when a field is
        // blurred after retyping the number already applied. Without this the panel
        // reloads on every stray click into and out of a field.
        if (next === new URL(window.location.href).toString()) return;
        window.location.assign(next);
      }

      urlFor() {
        var u = new URL(window.location.href);
        var p = u.searchParams;
        var a = Number(this.a.value), b = Number(this.b.value);

        if (this.kind === 'band') {
          var key = this.dataset.param;
          p.delete(key);
          // Only append when it is an actual narrowing; selecting everything should
          // clear the filter, not send thirteen redundant params.
          if (a > this.lo || b < this.hi) {
            for (var i = Math.round(a); i <= Math.round(b); i++) {
              if (this.stops[i]) p.append(key, this.stops[i].value);
            }
          }
        } else {
          p.delete(this.dataset.paramMin);
          p.delete(this.dataset.paramMax);
          if (a > this.lo) p.set(this.dataset.paramMin, a);
          if (b < this.hi) p.set(this.dataset.paramMax, b);
        }
        // Paging is meaningless once the result set changes underneath it.
        p.delete('page');
        return u.toString();
      }
    });

  /* ==========================================================================
     THE THEME'S SORT + DENSITY BAND MOVES INTO OUR OWN FOOT
     ==========================================================================
     Measured 2026-08-02: the theme renders its own band between this panel and the
     first product — 120px printing "1892 products", a "Clear all", the density
     buttons and the sort <select>. The count and the Clear all are BOTH already in
     our foot 60px above it, so the page stated the same number twice and then cost
     the shopper 120px of fold to do it.

     ⚠️ MOVED, NOT REBUILT. Rebuilding sort and density in our own markup would be a
     second implementation of theme behaviour that has to stay in step with it — the
     mistake `chain_fit` is still paying for. `appendChild` relocates the live nodes,
     so the theme's own listeners come with them: ViewModeController binds click
     handlers DIRECTLY to each button (it clones and replaces them first), and the
     sort <select> keeps working because the whole `facet-filters-form` custom
     element travels with it rather than being torn out of its host.

     ⚠️ THE BAND IS HIDDEN ONLY AFTER THE MOVE SUCCEEDS. Hiding it first, or hiding
     it on a page where the markup has shifted, would take the sort control off the
     storefront entirely with nothing standing in for it. Every step is guarded and
     the whole thing is a no-op if the nodes are not where it expects.

     ⚠️ If AJAX filtering is ever wired up (the theme's `FacetFiltersForm.renderPage`
     re-renders this region), this has to run again after each render or the band
     comes back and the relocation is lost. */
  function relocateTools() {
    var foot = document.querySelector('.sb__foot');
    var sort = document.querySelector('facet-filters-form.facets-vertical-sort');
    if (!foot || !sort) return;
    var band = sort.parentElement;
    if (!band || band.classList.contains('sb-relocated')) return;

    var tools = document.createElement('div');
    tools.className = 'sb__tools';
    // Scoped to the band, so a `.view-mode` living in some drawer elsewhere on the
    // page is left where it is.
    Array.prototype.forEach.call(band.querySelectorAll('.view-mode'), function (vm) {
      tools.appendChild(vm);
    });
    tools.appendChild(sort);

    var views = foot.querySelector('.sb__views');
    if (views) foot.insertBefore(tools, views);
    else foot.appendChild(tools);

    band.classList.add('sb-relocated');
  }

  /* ==========================================================================
     DROPDOWN FILTERS — the small part <details> does not do for itself
     ==========================================================================
     Opening, closing, keyboard operation and the expanded state are all native, so
     this adds only the two behaviours a floating popup needs and a bare <details>
     has no opinion about: close when the click lands elsewhere, and close on Escape.

     ⚠️ EVERYTHING HERE IS AN ENHANCEMENT, NEVER A REQUIREMENT. With this script dead
     the dropdowns still open, still close by clicking their own trigger again, and
     the filters inside still work — they are links and a slider that rewrites the
     URL. Do not move opening into JS. */
  function wireDropdowns() {
    var panel = document.querySelector('.sb');
    if (!panel) return;

    // One open at a time. Two overlapping popups in a wrapping row can cover each
    // other, and the second one's trigger is then unreachable.
    panel.addEventListener('toggle', function (e) {
      var dd = e.target;
      if (!dd.classList || !dd.classList.contains('sb-dd') || !dd.open) return;
      Array.prototype.forEach.call(panel.querySelectorAll('.sb-dd[open]'), function (other) {
        if (other !== dd) other.open = false;
      });
    }, true);   // capture: `toggle` does not bubble

    document.addEventListener('click', function (e) {
      Array.prototype.forEach.call(panel.querySelectorAll('.sb-dd[open]'), function (dd) {
        if (!dd.contains(e.target)) dd.open = false;
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = panel.querySelectorAll('.sb-dd[open]');
      if (!open.length) return;
      Array.prototype.forEach.call(open, function (dd) {
        dd.open = false;
        // Send focus back to the trigger rather than leaving it orphaned inside a
        // panel that is no longer on screen.
        var btn = dd.querySelector('.sb-dd__btn');
        if (btn && dd.contains(document.activeElement)) btn.focus();
      });
    });
  }

  function boot() { relocateTools(); wireDropdowns(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
